import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV_GROUPS = [];

const SIMPLE_LINKS = [];

function DesktopGroup({ group }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const openNow = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
    >
      <button
        type="button"
        data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.22em] font-medium transition-colors ${
          open ? "text-[var(--ml-gold)]" : "text-[var(--ml-text)] hover:text-[var(--ml-gold)]"
        }`}
        aria-expanded={open}
      >
        {group.label}
        <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`absolute top-full left-0 pt-4 transition-all duration-300 ${
          open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
        }`}
        style={{ minWidth: 240 }}
      >
        <div className="bg-[#0a0a0a] border border-white/10 backdrop-blur-xl py-3">
          {group.items.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-dropdown-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `block px-6 py-3 text-[0.72rem] uppercase tracking-[0.22em] transition-colors ${
                  isActive ? "text-[var(--ml-gold)]" : "text-[var(--ml-text)] hover:text-[var(--ml-gold)] hover:bg-white/5"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-700 ${
          scrolled ? "backdrop-blur-2xl bg-black/70 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="container-ml flex items-center justify-between py-5">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <span className="wordmark text-2xl md:text-3xl text-[var(--ml-text)] tracking-[0.18em]">
              Menorca<span className="text-[var(--ml-gold)]">·Luxury</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_GROUPS.map(g => (
              <DesktopGroup key={g.label} group={g} />
            ))}
            {SIMPLE_LINKS.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-line text-[0.7rem] uppercase tracking-[0.22em] font-medium transition-colors ${
                    isActive ? "text-[var(--ml-gold)]" : "text-[var(--ml-text)] hover:text-[var(--ml-gold)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href="https://booking.travelchannelworld.com/en/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-reserve-cta"
              className="border border-[var(--ml-gold)] text-[var(--ml-gold)] hover:bg-[var(--ml-gold)] hover:text-black px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.28em] font-semibold transition-all"
            >
              Reserve Now
            </a>
            <Link
              to="/concierge"
              data-testid="nav-concierge-cta"
              className="bg-[var(--ml-gold)] hover:bg-white text-black px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.28em] font-semibold transition-all"
            >
              Concierge
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(v => !v)}
            className="lg:hidden text-[var(--ml-text)]"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-30 bg-[var(--ml-bg)] transition-all duration-700 overflow-y-auto ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        } lg:hidden`}
      >
        <div className="container-ml pt-28 pb-12">
          {NAV_GROUPS.map(g => (
            <div key={g.label} className="mb-10">
              <p className="overline mb-5">{g.label}</p>
              <nav className="flex flex-col gap-4">
                {g.items.map(it => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={`mobile-nav-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-serif-ml text-3xl tracking-tight text-[var(--ml-text)] hover:text-[var(--ml-gold)] transition-colors"
                  >
                    {it.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}

          <div className="rule my-10" />

          <nav className="flex flex-col gap-4 mb-12">
            {SIMPLE_LINKS.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-serif-ml text-3xl tracking-tight text-[var(--ml-text)] hover:text-[var(--ml-gold)] transition-colors"
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <a
            href="https://booking.travelchannelworld.com/en/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-reserve-cta"
            className="border border-[var(--ml-gold)] text-[var(--ml-gold)] hover:bg-[var(--ml-gold)] hover:text-black px-8 py-4 text-[0.7rem] uppercase tracking-[0.28em] font-semibold inline-block mr-3"
          >
            Reserve Now
          </a>
          <Link
            to="/concierge"
            data-testid="mobile-nav-concierge-cta"
            className="bg-[var(--ml-gold)] hover:bg-white text-black px-8 py-4 text-[0.7rem] uppercase tracking-[0.28em] font-semibold inline-block"
          >
            Concierge
          </Link>
        </div>
      </div>
    </>
  );
}
